import React, { useState, useEffect } from "react";
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  InfoWindow,
} from "react-google-maps";
import { renderToStaticMarkup } from "react-dom/server";
import CustomSvg from "./locationSvg.js";

const he = require("he");

const GoogleMapComponent = ({ locations, locationId }) => {
  const defaultCenter =
    locationId === "29124"
      ? { lat: 37.798121, lng: -122.41345 }
      : { lat: 40.69710430478249, lng: -74.00809504509347 };
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const handleMapClick = () => {
    setSelectedMarker(null);
  };

  return (
    <>
      {windowWidth > 1024 ? (
        <GoogleMap
          defaultZoom={14}
          defaultCenter={defaultCenter}
          center={selectedMarker ? selectedMarker : defaultCenter}
          onClick={handleMapClick}
          fullscreenControl={false}
          streetViewControl={false}
          mapTypeControl={false}
          zoomControl={false}
          scaleControl={false}
          rotateControl={false}
        >
          {locations.map((location, index) => {
            const coordiantes = location.field_address.split(",");
            const lat = coordiantes[0];
            const lng = coordiantes[1];

            const svgString = encodeURIComponent(
              renderToStaticMarkup(<CustomSvg />)
            );
            const markerIcon = {
              url: `data:image/svg+xml;charset=UTF-8,${svgString}`,
              scaledSize: new window.google.maps.Size(70, 70), // Adjust size as needed
            };

            return (
              <Marker
                key={index}
                position={{
                  lat: Number(lat),
                  lng: Number(lng),
                }}
                onClick={() => handleMarkerClick(location)}
                icon={markerIcon}
              >
                {selectedMarker === location && (
                  <InfoWindow onCloseClick={() => setSelectedMarker(null)}>
                    <div className="map-info-box">
                      <div className="map-info-box__left">
                        <div className="map-info-box__left--address">
                          {location?.title ? he.decode(location.title) : ""}
                        </div>
                        <div className="map-info-box__left--sub">
                          {location?.field_subtitle
                            ? he.decode(location.field_subtitle)
                            : ""}
                        </div>
                        <div className="map-info-box__left--container">
                          <div className="map-info-box__left--location">
                            <a href={location?.field_get_d} target="_blank">
                              <svg
                                width="50"
                                height="50"
                                viewBox="0 0 50 50"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M27.0821 40.2624C26.8981 40.5394 26.6606 40.7768 26.3837 40.9609C25.2338 41.7251 23.682 41.4124 22.9178 40.2624C16.5282 30.6475 13.3333 23.8933 13.3333 19.9999C13.3333 13.5566 18.5567 8.33325 25 8.33325C31.4433 8.33325 36.6666 13.5566 36.6666 19.9999C36.6666 23.8933 33.4718 30.6475 27.0821 40.2624Z"
                                  fill="white"
                                />
                                <ellipse
                                  cx="25"
                                  cy="19.1667"
                                  rx="4.16667"
                                  ry="4.16667"
                                  fill="url(#paint0_linear_10_156845)"
                                />
                                <defs>
                                  <linearGradient
                                    id="paint0_linear_10_156845"
                                    x1="31.6006"
                                    y1="17.5426"
                                    x2="23.2673"
                                    y2="12.6746"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stopColor="#F42853" />
                                    <stop offset="1" stopColor="#FB5151" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </a>
                          </div>
                          <div className="map-info-box__left--3d">
                            <a
                              href={location?.field_get_direction_streetview_u}
                              target="_blank"
                            >
                              3D
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="map-info-box__right">
                        <img
                          className="map-info-box__right--img"
                          src={location?.field_image}
                        />
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            );
          })}
        </GoogleMap>
      ) : (
        <>
          {locations.map((location, index) => {
            return (
              <div className="mobile-map-info-box" key={index}>
                <div className="mobile-map-info-box__left">
                  <div className="mobile-map-info-box__left--address">
                    {location?.field_subtitle
                      ? he.decode(location.field_subtitle)
                      : ""}
                  </div>
                  <div className="mobile-map-info-box__left--sub">
                    {location?.title ? he.decode(location.title) : ""}
                  </div>
                  <div className="mobile-map-info-box__left--time">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 28 28"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14 27.3334C6.63622 27.3334 0.666687 21.3639 0.666687 14.0001C0.666687 6.63628 6.63622 0.666748 14 0.666748C21.3638 0.666748 27.3334 6.63628 27.3334 14.0001C27.3334 21.3639 21.3638 27.3334 14 27.3334ZM14 25.6668C20.4433 25.6668 25.6667 20.4434 25.6667 14.0001C25.6667 7.55676 20.4433 2.33341 14 2.33341C7.5567 2.33341 2.33335 7.55676 2.33335 14.0001C2.33335 20.4434 7.5567 25.6668 14 25.6668ZM19 14.8334H14.8334V7.33341C14.8334 6.87318 14.4603 6.50008 14 6.50008C13.5398 6.50008 13.1667 6.87318 13.1667 7.33341V15.6667C13.1667 16.127 13.5398 16.5001 14 16.5001H19C19.4603 16.5001 19.8334 16.127 19.8334 15.6667C19.8334 15.2065 19.4603 14.8334 19 14.8334Z"
                        fill="#F52A53"
                      />
                    </svg>
                    <div className="mobile-map-info-box--location-time">
                      {location?.field_open_time && location?.field_open_time}
                    </div>
                  </div>
                  <div className="mobile-map-info-box__left--container">
                    <div className="mobile-map-info-box__left--location">
                      <a href={location?.field_get_d}>
                        <svg
                          width="50"
                          height="50"
                          viewBox="0 0 50 50"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M27.0821 40.2624C26.8981 40.5394 26.6606 40.7768 26.3837 40.9609C25.2338 41.7251 23.682 41.4124 22.9178 40.2624C16.5282 30.6475 13.3333 23.8933 13.3333 19.9999C13.3333 13.5566 18.5567 8.33325 25 8.33325C31.4433 8.33325 36.6666 13.5566 36.6666 19.9999C36.6666 23.8933 33.4718 30.6475 27.0821 40.2624Z"
                            fill="white"
                          />
                          <ellipse
                            cx="25"
                            cy="19.1667"
                            rx="4.16667"
                            ry="4.16667"
                            fill="url(#paint0_linear_10_156845)"
                          />
                          <defs>
                            <linearGradient
                              id="paint0_linear_10_156845"
                              x1="31.6006"
                              y1="17.5426"
                              x2="23.2673"
                              y2="12.6746"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stopColor="#F42853" />
                              <stop offset="1" stopColor="#FB5151" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </a>
                    </div>
                    <div className="mobile-map-info-box__left--3d">
                      <a href={location?.field_get_direction_streetview_u}>
                        3D
                      </a>
                    </div>
                  </div>
                </div>
                <div className="mobile-map-info-box__right">
                  <img
                    className="mobile-map-info-box__right--img"
                    src={location?.field_image}
                  />
                </div>
              </div>
            );
          })}
        </>
      )}
    </>
  );
};

const WrappedMap =
  window.innerWidth > 1024
    ? withScriptjs(withGoogleMap(GoogleMapComponent))
    : GoogleMapComponent;

export default WrappedMap;
